import { useCallback, useEffect, useState } from 'react';
import { ApiError, formatChips, type Me } from '../api.js';

interface QueueEntry {
  id: string;
  title: string;
  who: string;
  cost: number;
  note: string;
  at: number;
}

interface FloorState {
  live: boolean;
  phase: string;
  bets: Array<{
    displayName: string;
    stake: number;
    status: string;
    payout: number;
    marketLabel: string;
    placedAt: number;
  }>;
  totalStaked: number;
  bettors: number;
}

const API_BASE = (import.meta.env['VITE_API_BASE'] as string | undefined) ?? '';

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const token = localStorage.getItem('hienbet.session');
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const problem = body as { message?: string; error?: string };
    throw new ApiError(problem.message ?? 'That did not work.', problem.error ?? 'error');
  }
  return body as T;
}

/**
 * Hien's fulfilment queue.
 *
 * Only she can load it (the server checks her Twitch id), and it is built to be
 * glanced at mid-stream: who, what, their note, two buttons. Add it as an OBS
 * custom browser dock and it lives next to her chat.
 *
 * Chat announcements tell her the moment something lands; this page is where
 * she acts on it. Declining refunds the viewer in full, automatically — she
 * should never feel forced to do anything.
 */
export function QueuePage({ me }: { me: Me | null }) {
  const [entries, setEntries] = useState<QueueEntry[] | null>(null);
  const [floor, setFloor] = useState<FloorState | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await call<{ pending: QueueEntry[] }>('/api/rewards/queue');
      setEntries(data.pending);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the queue.');
    }
    // The floor is glanceable extra; if it fails, the queue still works.
    try {
      setFloor(await call<FloorState>('/api/floor'));
    } catch {
      /* keep the last view */
    }
  }, []);

  useEffect(() => {
    if (!me?.isBroadcaster) return;
    void load();
    const timer = setInterval(load, 8_000);
    return () => clearInterval(timer);
  }, [me?.isBroadcaster, load]);

  if (!me?.loggedIn) {
    return (
      <div className="page">
        <p className="note">Log in first.</p>
      </div>
    );
  }

  if (!me.isBroadcaster) {
    return (
      <div className="page">
        <p className="note">This page is Hien&rsquo;s fulfilment queue — only she can see it.</p>
      </div>
    );
  }

  const act = async (id: string, action: 'fulfil' | 'decline') => {
    setBusy(id);
    try {
      await call(`/api/rewards/queue/${id}`, { method: 'POST', body: JSON.stringify({ action }) });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="page">
      <header className="page-head">
        <h1>To do on stream</h1>
        <p>
          Things viewers have paid for. Done it? Hit done. Not comfortable with one? Decline —
          they get every chip back automatically, no conversation needed.
        </p>
      </header>

      {error && <p className="slip-error">{error}</p>}
      {entries === null && !error && <p className="note">Loading…</p>}
      {entries?.length === 0 && (
        <p className="note">Nothing waiting. Redemptions appear here the moment they happen.</p>
      )}

      {entries && entries.length > 0 && (
        <ul className="queue">
          {entries.map((entry) => (
            <li key={entry.id}>
              <div className="queue-what">
                <span className="queue-title">{entry.title}</span>
                <span className="queue-who">
                  from <strong>{entry.who}</strong> · {formatChips(entry.cost)} chips
                </span>
                {entry.note && <span className="queue-note">&ldquo;{entry.note}&rdquo;</span>}
              </div>
              <div className="queue-actions">
                <button
                  className="queue-done"
                  disabled={busy === entry.id}
                  onClick={() => void act(entry.id, 'fulfil')}
                >
                  Done
                </button>
                <button
                  className="ghost"
                  disabled={busy === entry.id}
                  onClick={() => void act(entry.id, 'decline')}
                >
                  Decline &amp; refund
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <h2 className="section">The floor</h2>
      {!floor?.live && (
        <p className="note">No game on the board. The moment one opens, every bet lands here live.</p>
      )}
      {floor?.live && (
        <>
          <p className="note">
            <strong className="num">{formatChips(floor.totalStaked)}</strong> chips riding from{' '}
            <strong className="num">{floor.bettors}</strong> bettor{floor.bettors === 1 ? '' : 's'} this
            game.
          </p>
          <ul className="floor">
            {floor.bets.map((bet, i) => (
              <li key={`${bet.placedAt}-${i}`} className={bet.status}>
                <span className="floor-who">{bet.displayName}</span>
                <span className="num floor-stake">{formatChips(bet.stake)}</span>
                <span className="floor-market">{bet.marketLabel}</span>
                <span className={`floor-status ${bet.status}`}>
                  {bet.status === 'open' ? 'riding' : bet.status}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      <p className="shop-fine">
        Tip: OBS &rarr; Docks &rarr; Custom Browser Docks &rarr; paste this page&rsquo;s address,
        and the queue and the floor sit next to your chat while you stream.
      </p>
    </div>
  );
}
